/**
 * "Certificados e carimbo" (E157.F3, GUS-157, ADR-0107 §2.7): por peça, a tabela Ponto / Leitura
 * ou situação / Fonte composta no servidor — a assinatura embutida, a cadeia de confiança, a
 * revogação na data pertinente, o carimbo do tempo, o certificado de atributo (quando há
 * manifesto), os códigos lidos das páginas e a aparência de assinatura. "Ver fonte e detalhes
 * técnicos" abre, na linha, a fonte e os pares rótulo/valor (certificados do caminho, material de
 * revogação com janela, algoritmos); o limite do método fecha a tela. Nada aqui recalcula: a tela
 * mostra o que o servidor compôs sobre o resultado persistido do exame.
 */
import { AlertTriangle, CheckCircle2, CircleDashed, CircleHelp, XCircle } from 'lucide-react';
import { useState } from 'react';

import {
  Badge,
  Button,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  type BadgeProps,
} from '@/components/ui';
import { ptBR } from '@/i18n/pt-BR';
import type {
  CertificadosECarimboData,
  LinhaDaConferenciaData,
  PecaConferidaData,
  SituacaoDoPonto,
} from '@/lib/certificados_e_carimbo';

import { VinculoDoCarimbo } from './VinculoDoCarimbo';

const t = ptBR.certificadosECarimbo;

const SITUACAO_META: Record<
  SituacaoDoPonto,
  { variant: NonNullable<BadgeProps['variant']>; icon: typeof CheckCircle2 }
> = {
  confere: { variant: 'success', icon: CheckCircle2 },
  nao_confere: { variant: 'danger', icon: XCircle },
  ausente: { variant: 'warning', icon: CircleDashed },
  nao_conferivel: { variant: 'warning', icon: AlertTriangle },
  nao_executado: { variant: 'neutral', icon: CircleHelp },
};

function preencher(texto: string, valores: Record<string, string>): string {
  return Object.entries(valores).reduce(
    (acumulado, [chave, valor]) => acumulado.replaceAll(`{${chave}}`, valor),
    texto,
  );
}

export function SituacaoDoPontoBadge({ situacao }: { situacao: SituacaoDoPonto }): JSX.Element {
  const meta = SITUACAO_META[situacao];
  const Icon = meta.icon;
  return (
    <Badge
      variant={meta.variant}
      icon={<Icon className="h-3 w-3" aria-hidden />}
      data-situacao={situacao}
    >
      {t.situacao[situacao]}
    </Badge>
  );
}

interface LinhaProps {
  linha: LinhaDaConferenciaData;
  idDosDetalhes: string;
  aberta: boolean;
  onAlternar: () => void;
}

function Linha({ linha, idDosDetalhes, aberta, onAlternar }: LinhaProps): JSX.Element {
  return (
    <>
      <TableRow
        data-testid="certificados-linha"
        data-ponto={linha.ponto}
        data-campo={linha.campo ?? ''}
        data-situacao={linha.situacao}
      >
        <TableCell className="w-[22%] align-top">
          <div className="font-medium text-fg">{t.ponto[linha.ponto]}</div>
          {linha.campo !== null ? (
            <div className="text-caption text-fg-muted">
              {preencher(t.peca.campo, { campo: linha.campo })}
            </div>
          ) : null}
        </TableCell>
        <TableCell className="align-top">
          <div className="mb-1">
            <SituacaoDoPontoBadge situacao={linha.situacao} />
          </div>
          <p className="text-body-sm text-fg" data-testid="certificados-leitura">
            {linha.leitura}
          </p>
        </TableCell>
        <TableCell className="w-[28%] align-top">
          <p className="text-body-sm text-fg-muted" data-testid="certificados-fonte">
            {linha.fonte}
          </p>
          <Button
            type="button"
            variant="link"
            size="sm"
            className="mt-1"
            aria-expanded={aberta}
            aria-controls={idDosDetalhes}
            onClick={onAlternar}
            data-testid="certificados-detalhes-alternar"
          >
            {aberta ? t.detalhes.fechar : t.detalhes.abrir}
          </Button>
        </TableCell>
      </TableRow>
      {aberta ? (
        <TableRow data-testid="certificados-detalhes">
          <TableCell colSpan={3} id={idDosDetalhes} className="bg-surface-2">
            <h5 className="text-caption font-medium uppercase tracking-[0.06em] text-fg-muted">
              {t.detalhes.titulo}
            </h5>
            <dl className="mt-sc-2 grid grid-cols-[minmax(10rem,auto)_1fr] gap-x-sc-4 gap-y-1 text-body-sm">
              <dt className="text-fg-muted">{t.detalhes.fonte}</dt>
              <dd className="text-fg">{linha.fonte}</dd>
              {linha.detalhes.map((detalhe, i) => (
                <DetalheItem
                  key={`${detalhe.rotulo}-${i}`}
                  rotulo={detalhe.rotulo}
                  valor={detalhe.valor}
                />
              ))}
            </dl>
            {linha.detalhes.length === 0 ? (
              <p className="mt-sc-2 text-body-sm text-fg-muted">{t.detalhes.vazio}</p>
            ) : null}
          </TableCell>
        </TableRow>
      ) : null}
    </>
  );
}

function DetalheItem({ rotulo, valor }: { rotulo: string; valor: string }): JSX.Element {
  return (
    <>
      <dt className="text-fg-muted">{rotulo}</dt>
      <dd className="break-words font-mono text-[13px] text-fg" data-testid="certificados-detalhe">
        {valor}
      </dd>
    </>
  );
}

function tituloDaPeca(peca: PecaConferidaData): string {
  if (peca.letra === null) return preencher(t.peca.semLetra, { nome: peca.nome });
  return preencher(t.peca.titulo, { letra: peca.letra, nome: peca.nome });
}

function PecaConferida({ peca }: { peca: PecaConferidaData }): JSX.Element {
  const [abertas, setAbertas] = useState<ReadonlySet<number>>(new Set());
  const alternar = (indice: number): void =>
    setAbertas((atuais) => {
      const proximas = new Set(atuais);
      if (proximas.has(indice)) proximas.delete(indice);
      else proximas.add(indice);
      return proximas;
    });
  const resumo = [
    peca.regua !== null ? preencher(t.peca.regua, { regua: peca.regua }) : null,
    peca.veredito !== null
      ? preencher(t.peca.veredito, { veredito: t.veredito[peca.veredito] })
      : null,
  ].filter((parte): parte is string => parte !== null);
  return (
    <section
      className="border border-border bg-surface p-sc-4"
      data-testid="certificados-peca"
      data-documento={peca.documento_id}
      aria-label={tituloDaPeca(peca)}
    >
      <header className="mb-sc-3">
        <h2 className="text-heading-sm text-fg">{tituloDaPeca(peca)}</h2>
        {resumo.length > 0 ? (
          <p className="text-caption text-fg-muted" data-testid="certificados-peca-resumo">
            {resumo.join(' ')}
          </p>
        ) : null}
      </header>
      {!peca.verificada ? (
        <p className="text-body-sm text-fg-muted" data-testid="certificados-sem-exame">
          {t.peca.semExame}
        </p>
      ) : (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>{t.colunas.ponto}</TableHead>
              <TableHead>{t.colunas.leitura}</TableHead>
              <TableHead>{t.colunas.fonte}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {peca.linhas.map((linha, i) => (
              <Linha
                key={`${linha.ponto}-${linha.campo ?? ''}-${i}`}
                linha={linha}
                idDosDetalhes={`certificados-detalhes-${peca.documento_id}-${i}`}
                aberta={abertas.has(i)}
                onAlternar={() => alternar(i)}
              />
            ))}
          </TableBody>
        </Table>
      )}
    </section>
  );
}

export function CertificadosECarimbo({ visao }: { visao: CertificadosECarimboData }): JSX.Element {
  if (visao.pecas.length === 0) {
    return (
      <p className="text-body-sm text-fg-muted" data-testid="certificados-vazio">
        {t.vazio}
      </p>
    );
  }
  return (
    <div className="flex flex-col gap-sc-4" data-testid="certificados-pecas">
      {visao.pecas.map((peca) => (
        <PecaConferida key={peca.documento_id} peca={peca} />
      ))}
      {visao.vinculo ? <VinculoDoCarimbo vinculo={visao.vinculo} /> : null}
      <footer className="border-t border-border pt-sc-3">
        <p className="text-body-sm text-fg-muted" data-testid="certificados-limite">
          <span className="font-medium text-fg">{t.rodape.limite}: </span>
          {visao.limite}
        </p>
      </footer>
    </div>
  );
}
